import { useLocation, useNavigate } from 'react-router';
import { Button, Divider, Slot, Text, Tooltip, mergeClasses } from '@fluentui/react-components';
import {
    NavCategory,
    NavCategoryItem,
    NavDrawer,
    NavDrawerBody,
    NavDrawerHeader,
    NavItem,
    NavSubItem,
    NavSubItemGroup,
} from '@fluentui/react-nav-preview';
import { makeStyles, tokens } from '@fluentui/react-components';
import {
    AppRecent20Filled,
    AppRecent20Regular,
    Board20Filled,
    Board20Regular,
    Briefcase20Filled,
    Briefcase20Regular,
    bundleIcon,
    ChevronDoubleLeftRegular,
    ChevronDoubleRightRegular,
    ChevronRight16Filled,
    DataTrending20Filled,
    DataTrending20Regular,
    People20Filled,
    People20Regular,
    PuzzleCubePiece20Filled,
    PuzzleCubePiece20Regular,
    Settings20Filled,
    Settings20Regular,
} from '@fluentui/react-icons';
import { Paths } from '../common/routes';
import Logo, { CasePortalLogo } from '../common/appLogo';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { Column, ColumnProps, Row } from '../common';
import { useAccess } from '../../auth/useAccess';
import { useCaseService, useThemeService } from '../../data/services';
import { useRecentCasesService } from '../../data/services/useRecentCasesService';
import React from 'react';
import { useMediaSizes } from '../common/Styles/useMediaSizes';
import { appTokens } from '../common/Styles/appTokens';
import { useClientPortalState } from '../../redux/reducers/clientPortalReducer';

const useStyles = makeStyles({
    root: {
        borderRight: `1px solid ${appTokens.verticalMenu.rightBorderColor}`,
        overflowX: 'hidden',
        overflowY: 'hidden',
        display: 'flex',
        height: '600px',
        '& >:focus': {
            outlineWidth: '0px',
            boxShadow: 'none',
        },
    },
    collapsed: {
        borderRight: 'none',
    },
    navDrawer: {
        backgroundColor: appTokens.verticalMenu.background,
    },
    railDrawer: {
        width: appTokens.verticalMenu.railWidth,
        minWidth: appTokens.verticalMenu.railWidth,
    },
    navDrawerHeader: {
        height: appTokens.menuHeight,
        paddingBlock: '0rem',
        paddingInline: '0rem',
        //border: '1px solid red',
    },
    collapseButton: {
        color: appTokens.verticalMenu.menuItemColor,
        ':hover': {
            color: appTokens.verticalMenu.menuItemColorHover,
            backgroundColor: appTokens.verticalMenu.menuItemBackgroundHover,
        },
    },
    navItem: {
        backgroundColor: appTokens.verticalMenu.menuItemBackground,
        color: appTokens.verticalMenu.menuItemColor,
        alignItems: 'center',
        width: '100%',
        textWrap: 'nowrap',
        overflowX: 'clip',
        textOverflow: 'ellipsis',
        borderRadius: '18px',
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
            backgroundColor: appTokens.verticalMenu.menuItemBackgroundSelected,
        },
        ':hover': {
            backgroundColor: appTokens.verticalMenu.menuItemBackgroundHover,
        },
        '::after': {
            content: 'none',
        },
    },
    subNavItem: {
        fontSize: '.85em',
    },
    navItemRail: {
        justifyContent: 'center',
        paddingInline: '0rem',
        minWidth: '0px',
    },
    navItemSelected: {
        backgroundColor: appTokens.verticalMenu.menuItemBackgroundSelected,
        color: appTokens.verticalMenu.menuItemColorSelected,
        ':hover': {
            backgroundColor: appTokens.verticalMenu.menuItemBackgroundSelected, //appTokens.verticalMenu.menuItemBackgroundHover
        },
        fontWeight: 'normal',
    },
    navBody: {
        paddingBlockStart: '.5rem',
        backgroundColor: appTokens.verticalMenu.background,
        //border: '1px solid red',
    },
    iconSelected: {
        '> *': {
            color: tokens.colorBrandBackground2Pressed,
        },
    },
    iconSelectedDarkMode: {
        '> *': {
            color: tokens.colorBrandForeground2,
        },
    },
    divider: {
        maxHeight: '1.5rem',
        color: ' white',
    },
});

export type VerticalNavMenuProps = {
    setIsOpen?: (open: boolean) => void;
    open?: boolean;
    className?: string;
};
export const VerticalNavMenu = (props: VerticalNavMenuProps) => {
    const { open = true, setIsOpen, className } = props;

    //Hooks
    const location = useLocation();
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const themes = useThemeService();
    const mediaSizes = useMediaSizes();
    const portalState = useClientPortalState();

    //Styles
    const styles = useStyles();
    // Below laptop the whole nav is hidden; above that the toggle switches between full width and an icon rail
    const isRail = !open && !mediaSizes.isUnderLaptop;
    const vertNavClasses = mergeClasses(styles.root, mediaSizes.isUnderLaptop && styles.collapsed, className);

    //Access
    const canSeeTemplates = () => {
        return !!primaryAccount?.account?.features?.templates && access.templates.canSeeTemplates();
    };
    const canSeeParties = () => {
        return access.settings.canAddEditParties() || access.settings.canDeleteParties();
    };
    const canSeeInsights = () => {
        return !!primaryAccount?.account.features?.insights && access.account.isAccountAdmin();
    };
    const canSeeSettings = () => {
        return access.settings.canAccessAccountSettings();
    };
    const canSeeAccountSection = () => {
        return canSeeParties() || canSeeInsights() || canSeeTemplates() || canSeeSettings();
    };

    //UI Events
    const clickToggle = (e: React.MouseEvent) => {
        e.stopPropagation();

        setIsOpen?.(!open);
    };

    //Logic
    const getIconClasses = (val: string) => {
        const iconClases = mergeClasses(
            val === location.pathname && themes.isLightTheme && styles.iconSelected,
            val === location.pathname && themes.isDarkTheme && styles.iconSelectedDarkMode,
        );
        return iconClases;
    };
    const getPath = () => {
        return location.pathname;
    };

    //Icons
    const DashboardIcon = bundleIcon(Board20Filled, Board20Regular);

    const portalId = portalState.portals[0]?.portalId;
    if (!portalId) return null;
    const portalPath = Paths.index(portalId);

    return (
        <div className={vertNavClasses}>
            <NavDrawer
                className={mergeClasses(styles.navDrawer, isRail && styles.railDrawer)}
                selectedValue={getPath()}
                open={open || isRail}
                type='inline'
                onOpenChange={(_, { open }) => !!setIsOpen && setIsOpen(open)}
                size='small'
            >
                <NavDrawerHeader className={styles.navDrawerHeader}>
                    <Row noGap noWrap>
                        {!isRail && (
                            <Column flex='1' minWidth='0px' vAlign='middle' align='center' padding='py-1 pl-3'>
                                {/* <Logo white /> */}
                                <CasePortalLogo white />
                            </Column>
                        )}
                        {!mediaSizes.isUnderLaptop && !!setIsOpen && (
                            <Column
                                flex={isRail ? '1' : '0 0 auto'}
                                vAlign='middle'
                                align={isRail ? 'center' : 'right'}
                                padding={`pt-1 ${isRail ? undefined : 'pr-1'}`}
                            >
                                <Tooltip
                                    content={isRail ? 'Expand navigation' : 'Collapse navigation'}
                                    relationship='label'
                                >
                                    <Button
                                        className={styles.collapseButton}
                                        icon={isRail ? <ChevronDoubleRightRegular /> : <ChevronDoubleLeftRegular />}
                                        appearance='transparent'
                                        onClick={clickToggle}
                                        aria-expanded={open}
                                        data-testid='vert-nav-collapse-button'
                                    />
                                </Tooltip>
                            </Column>
                        )}
                    </Row>
                </NavDrawerHeader>
                <NavDrawerBody className={styles.navBody}>
                    <Column height='98%'>
                        <Column gap='small' height='auto'>
                            <VertNavItem
                                path={portalPath}
                                text='Case Info'
                                collapsed={isRail}
                                icon={<DashboardIcon className={getIconClasses(portalPath)} />}
                                testId='vert-nav-dashboard'
                            />
                        </Column>
                        <Column></Column>
                        <Column height='auto'>
                        {!isRail && (
                            <Logo white />
                        )}
                        </Column>
                    </Column>
                </NavDrawerBody>
            </NavDrawer>
        </div>
    );
};

const useSectionStyles = makeStyles({
    headingContent: {
        color: 'white',
    },
    divider: {
        maxHeight: '1.5rem',
        color: ' white',
    },
});
type NavItemSectionProps = ColumnProps & {
    title?: string;
    children: React.ReactNode;
};
export const NavItemSection = (props: NavItemSectionProps) => {
    const { title, children, ...otherProps } = props;

    //Styles
    const styles = useSectionStyles();

    return (
        <Column gap='small' {...otherProps}>
            <Divider className={styles.divider} appearance='brand'>
                {title}
            </Divider>
            {children}
        </Column>
    );
};

export interface VertNavItemProps {
    path: string;
    icon?: Slot<'span'>;
    text: string;
    collapsed?: boolean;
    onClick?: () => void;
    testId?: string;
}
export const VertNavItem = (props: VertNavItemProps) => {
    const { path, icon, text, collapsed = false, onClick, testId } = props;

    //Hooks
    const navigate = useNavigate();
    const location = useLocation();

    //Styles
    const styles = useStyles();

    //UI events
    const clickNavItem = (e: any) => {
        e.stopPropagation();

        if (onClick) {
            onClick();
            return;
        }
        navTo(path);
    };

    //Logic
    const getNavItemClasses = (val: string) => {
        const navItemClasses = mergeClasses(
            styles.navItem,
            collapsed && styles.navItemRail,
            val === location.pathname && styles.navItemSelected,
        );
        return navItemClasses;
    };
    const navTo = (to: string) => {
        navigate(to);
    };

    const navItem = (
        <NavItem className={getNavItemClasses(path)} onClick={clickNavItem} icon={icon} value={path} data-testid={testId}>
            {!collapsed && text}
        </NavItem>
    );

    return collapsed ? (
        <Tooltip content={text} relationship='label' positioning='after'>
            {navItem}
        </Tooltip>
    ) : (
        navItem
    );
};

export interface VertSubNavItemProps {
    path: string;
    text: string;
    testId?: string;
}
export const VertSubNavItem = (props: VertSubNavItemProps) => {
    const { path, text, testId } = props;

    //Hooks
    const navigate = useNavigate();
    const location = useLocation();

    //Styles
    const styles = useStyles();

    //UI events
    const clickSubNavItem = (e: any) => {
        e.stopPropagation();

        navTo(path);
    };

    //Logic
    const getNavItemClasses = (val: string) => {
        const navItemClasses = mergeClasses(
            styles.navItem,
            styles.subNavItem,
            val === location.pathname && styles.navItemSelected,
        );
        return navItemClasses;
    };
    const navTo = (to: string) => {
        navigate(to);
    };

    return (
        <NavSubItem
            className={getNavItemClasses(path)}
            onClick={clickSubNavItem}
            value={path.replace(/ /g, '%20')}
            data-testid={testId}
        >
            <Text style={{ fontSize: '1em', textOverflow: 'ellipsis' }} wrap={false}>
                {text}
            </Text>
        </NavSubItem>
    );
};

export interface VertNavCategoryProps {
    path?: string;
    icon?: Slot<'span'>;
    text: string;
    children?: React.ReactNode;
    testId?: string;
}
export const VertNavCategory = (props: VertNavCategoryProps) => {
    const { path, text, icon, children, testId } = props;

    //Styles
    const styles = useStyles();

    return (
        <>
            <NavCategory value={path}>
                <NavCategoryItem
                    className={styles.navItem}
                    icon={icon}
                    expandIcon={<ChevronRight16Filled />}
                    data-testid={testId}
                >
                    {text}
                </NavCategoryItem>
                <NavSubItemGroup>{children}</NavSubItemGroup>
            </NavCategory>
        </>
    );
};
